import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import StepIndicator from './components/StepIndicator';
import RecipientSelection from './components/RecipientSelection';
import QuoteCalculator from './components/QuoteCalculator';
import ComplianceForm from './components/ComplianceForm';
import TransferReview from './components/TransferReview';
import TransferSummary from './components/TransferSummary';

const SendMoneyFlow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);

  // Mock user data
  const mockUser = {
    id: 1,
    name: "John Smith",
    email: "john.smith@email.com",
    role: "customer"
  };

  const steps = [
    {
      id: 1,
      title: "Select Recipient",
      description: "Choose who to send to"
    },
    {
      id: 2,
      title: "Get Quote",
      description: "Calculate transfer amount"
    },
    {
      id: 3,
      title: "Compliance",
      description: "Provide required information"
    },
    {
      id: 4,
      title: "Review & Confirm",
      description: "Final confirmation"
    }
  ];

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('sendMoneyFlowData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setSelectedRecipient(parsedData?.selectedRecipient);
        setQuoteData(parsedData?.quoteData);
        setComplianceData(parsedData?.complianceData);
        setCurrentStep(parsedData?.currentStep || 1);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever state changes
  useEffect(() => {
    const dataToSave = {
      currentStep,
      selectedRecipient,
      quoteData,
      complianceData
    };
    localStorage.setItem('sendMoneyFlowData', JSON.stringify(dataToSave));
  }, [currentStep, selectedRecipient, quoteData, complianceData]);

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRecipientSelect = (recipient) => {
    setSelectedRecipient(recipient);
  };

  const handleQuoteUpdate = (quote) => {
    setQuoteData(quote);
  };

  const handleComplianceUpdate = (compliance) => {
    setComplianceData(compliance);
  };

  const handleLogout = () => {
    // Clear saved flow data on logout
    localStorage.removeItem('sendMoneyFlowData');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <RecipientSelection
            onNext={handleNext}
            selectedRecipient={selectedRecipient}
            onSelectRecipient={handleRecipientSelect}
          />
        );
      case 2:
        return (
          <QuoteCalculator
            recipient={selectedRecipient}
            onNext={handleNext}
            onBack={handleBack}
            quoteData={quoteData}
            onQuoteUpdate={handleQuoteUpdate}
          />
        );
      case 3:
        return (
          <ComplianceForm
            recipient={selectedRecipient}
            quote={quoteData}
            onNext={handleNext}
            onBack={handleBack}
            complianceData={complianceData}
            onComplianceUpdate={handleComplianceUpdate}
          />
        );
      case 4:
        return (
          <TransferReview
            recipient={selectedRecipient}
            quote={quoteData}
            complianceData={complianceData}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} onLogout={handleLogout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3">
            <StepIndicator currentStep={currentStep} steps={steps} />
            <div className="bg-background">
              {renderCurrentStep()}
            </div>
          </div>

          {/* Sidebar - Transfer Summary */}
          <div className="xl:col-span-1">
            <div className="lg:sticky lg:top-8">
              <TransferSummary
                recipient={selectedRecipient}
                quote={quoteData}
                currentStep={currentStep}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendMoneyFlow;